/**
 * The deepest schema node or value node the validator walks. The publish door caps declared
 * schemas at depth 16 (`SURFACE_SCHEMA_MAX_DEPTH`); this larger bound only stops a hostile or
 * cyclic value from exhausting the stack.
 */
export const VALIDATOR_MAX_DEPTH = 64;
/**
 * The largest `maxLength` a string node may declare: 4096, the value of `SURFACE_STRING_MAX_LENGTH`
 * in `@zion/sdk-contracts` (R-36). The runtime holds its own copy because it has no runtime
 * dependency (D12, R-53); `validate.test.ts` pins the literal and asserts it equals the contract's.
 */
export const VALIDATOR_MAX_STRING_LENGTH = 4096;
/**
 * The source of each named `pattern` the type language admits, keyed by name: the runtime's copy of
 * `SURFACE_PATTERNS` in `@zion/sdk-contracts` (R-54). A declaration names one of these keys and never
 * carries a regular expression of its own, so the runtime runs only these seven expressions over a
 * host's input. Each is anchored at both ends and linear: no quantified group overlaps the text that
 * follows it, so a match costs one pass over a subject already bounded by its node's `maxLength`.
 * The runtime holds its own copy because it has no runtime dependency (D12, R-53), as it does for
 * {@link VALIDATOR_MAX_STRING_LENGTH}; `validate.test.ts` pins every source and asserts each equals the
 * contract's. The object has no prototype and is frozen, so an inherited name such as `constructor`
 * or `__proto__` is never a pattern.
 */
export const VALIDATOR_PATTERN_SOURCES = Object.freeze(Object.assign(Object.create(null), {
    'hex-color': '^#[0-9a-fA-F]{6}$',
    slug: '^[a-z0-9][a-z0-9-]*[a-z0-9]$',
    uuid: '^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$',
    url: '^https://[A-Za-z0-9-]+(?:\\.[A-Za-z0-9-]+)*(?::[0-9]{1,5})?(?:[/?#][!-~]*)?$',
    email: '^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\\.[A-Za-z0-9-]+)+$',
    'iso-date': '^[0-9]{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12][0-9]|3[01])$',
    semver: '^(?:0|[1-9][0-9]*)\\.(?:0|[1-9][0-9]*)\\.(?:0|[1-9][0-9]*)(?:-[0-9A-Za-z-]+(?:\\.[0-9A-Za-z-]+)*)?(?:\\+[0-9A-Za-z-]+(?:\\.[0-9A-Za-z-]+)*)?$',
}));
/** The flags every named pattern compiles with: `u`, as Ajv compiles `pattern`. No `g` or `y`, so a test keeps no state. */
export const VALIDATOR_PATTERN_FLAGS = 'u';
/**
 * The compiled table, built once when the module loads and never exported, so no caller can reach
 * an expression and recompile it. A name the table lacks is not an own key, and the lookup answers
 * `undefined`.
 */
const COMPILED_PATTERNS = new Map(Object.entries(VALIDATOR_PATTERN_SOURCES).map(([name, source]) => [name, new RegExp(source, VALIDATOR_PATTERN_FLAGS)]));
const ANNOTATIONS = new Set(['description', 'default', '$schema']);
const KEYWORDS_BY_TYPE = {
    string: new Set(['type', 'enum', 'const', 'maxLength', 'pattern']),
    number: new Set(['type']),
    boolean: new Set(['type']),
    null: new Set(['type']),
    object: new Set(['type', 'properties', 'required', 'additionalProperties']),
    array: new Set(['type', 'items']),
};
/**
 * Validate `value` against `schema`, a JSON Schema node in the closed type language.
 *
 * `schema` is untrusted JSON from a declaration. The validator reads the keywords
 * `surface-type.schema.json` admits: `type` over the six JSON types, `properties`, `required`,
 * `additionalProperties: false`, `items`, a string `enum`, a string `const`, a string's `maxLength`
 * and `pattern`, a `oneOf` of variants, and `{}` for `unknown`. A free string node, one with no
 * `enum` and no `const`, must carry `maxLength` (R-36); an `enum` or `const` node is bounded by its
 * members and may carry one, checked when present (R-52). `pattern` is a name from
 * {@link VALIDATOR_PATTERN_SOURCES} (R-54). The runtime looks the name up and runs that table's
 * expression, compiled once when the module loads; a declaration never supplies an expression of
 * its own. The expression runs only on a string that already passed its length bound or its `enum`
 * or `const`, so the subject is at most {@link VALIDATOR_MAX_STRING_LENGTH} code points. `description`, `default` and
 * `$schema` are annotations and are ignored; the surface applies its own defaults (WS-2 Q-8).
 *
 * Outcomes, checked in this order:
 * - `not-cloneable`: the value holds something JSON cannot carry: a function, a symbol, a bigint,
 *   a DOM node, a class instance, a `Date`, `undefined` in an array, or nesting past
 *   {@link VALIDATOR_MAX_DEPTH} (which a cycle reaches). An `undefined` object member counts as
 *   absent.
 * - `type`: a `NaN` or infinite number anywhere in the value.
 * - Then, node by node: `schema-unreadable` for a schema node that is not a plain object, carries
 *   a keyword outside its type's set, names an unknown `type`, an `object` whose
 *   `additionalProperties` is not `false`, a `required` name with no property, a free string node
 *   with no `maxLength`, a `maxLength` that is not an integer from 0 to
 *   {@link VALIDATOR_MAX_STRING_LENGTH}, a `pattern` that is not a string or names no own key of
 *   {@link VALIDATOR_PATTERN_SOURCES} (a regular expression such as `^#[0-9a-f]{6}$`, an inherited
 *   name such as `constructor`, a name in another case), or nesting past
 *   {@link VALIDATOR_MAX_DEPTH}. A node is read before the value under it.
 * - `type`: a value of the wrong JSON type.
 * - `length`: a string longer than its node's `maxLength`, counted in Unicode code points as JSON
 *   Schema and Ajv count them (R-52, R-53).
 * - `required`: a required key is absent. `enum`, `const`: a string outside the enum or unequal to
 *   the const. `pattern`: a string the named table expression does not match (R-53, R-54). `no-variant`: no `oneOf` variant accepts the value.
 *
 * A value the walk cannot read is `not-cloneable` at path `''`: a getter that throws, a `Proxy`
 * whose trap throws, a revoked `Proxy`. A schema the walk cannot read for the same reasons is
 * `schema-unreadable` at path `''`. The walk reads each value member once per copy and reads the
 * schema only after the copy, so the schema check never touches the caller's value.
 *
 * Tolerance follows D21: required keys must be present and valid, undeclared keys are dropped.
 * Pure: no I/O, no mutation of either argument, never throws.
 */
export function validateSurfaceValue(schema, value) {
    let copied;
    try {
        copied = copyJson(value, '', 0);
    }
    catch {
        return reject('', 'not-cloneable');
    }
    if (!copied.ok)
        return copied;
    try {
        return check(schema, copied.value, '', 0);
    }
    catch {
        return reject('', 'schema-unreadable');
    }
}
function reject(path, reason) {
    return { ok: false, path, reason };
}
function isPlainObject(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value))
        return false;
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
}
/** One JSON pointer reference token (RFC 6901). */
function token(key) {
    return key.replaceAll('~', '~0').replaceAll('/', '~1');
}
/** A fresh deep copy holding only JSON values, or the first node JSON cannot carry. */
function copyJson(value, path, depth) {
    if (depth > VALIDATOR_MAX_DEPTH)
        return reject(path, 'not-cloneable');
    if (value === null || typeof value === 'string' || typeof value === 'boolean')
        return { ok: true, value };
    if (typeof value === 'number')
        return Number.isFinite(value) ? { ok: true, value } : reject(path, 'type');
    if (value === undefined && depth === 0)
        return { ok: true, value };
    if (Array.isArray(value)) {
        const out = [];
        for (let i = 0; i < value.length; i += 1) {
            const item = value[i];
            if (item === undefined)
                return reject(`${path}/${i}`, 'not-cloneable');
            const copied = copyJson(item, `${path}/${i}`, depth + 1);
            if (!copied.ok)
                return copied;
            out.push(copied.value);
        }
        return { ok: true, value: out };
    }
    if (isPlainObject(value)) {
        const out = {};
        for (const key of Object.keys(value)) {
            const member = value[key];
            if (member === undefined)
                continue;
            const copied = copyJson(member, `${path}/${token(key)}`, depth + 1);
            if (!copied.ok)
                return copied;
            Object.defineProperty(out, key, { value: copied.value, enumerable: true, writable: true, configurable: true });
        }
        return { ok: true, value: out };
    }
    return reject(path, 'not-cloneable');
}
/** True when every own key of `node` is in `allowed` or an annotation. */
function keywordsAllowed(node, allowed) {
    return Object.keys(node).every((key) => allowed.has(key) || ANNOTATIONS.has(key));
}
function check(schema, value, path, depth) {
    if (depth > VALIDATOR_MAX_DEPTH || !isPlainObject(schema))
        return reject(path, 'schema-unreadable');
    if (Object.hasOwn(schema, 'oneOf'))
        return checkUnion(schema, value, path, depth);
    if (!Object.hasOwn(schema, 'type')) {
        return keywordsAllowed(schema, new Set()) ? { ok: true, value } : reject(path, 'schema-unreadable');
    }
    const type = schema['type'];
    const allowed = typeof type === 'string' && Object.hasOwn(KEYWORDS_BY_TYPE, type) ? KEYWORDS_BY_TYPE[type] : null;
    if (allowed === null || !keywordsAllowed(schema, allowed))
        return reject(path, 'schema-unreadable');
    switch (type) {
        case 'string':
            return checkString(schema, value, path);
        case 'number':
            return typeof value === 'number' ? { ok: true, value } : reject(path, 'type');
        case 'boolean':
            return typeof value === 'boolean' ? { ok: true, value } : reject(path, 'type');
        case 'null':
            return value === null ? { ok: true, value } : reject(path, 'type');
        case 'object':
            return checkObject(schema, value, path, depth);
        default:
            return checkArray(schema, value, path, depth);
    }
}
/** True when `max` is a `maxLength` the language admits: an integer from 0 to {@link VALIDATOR_MAX_STRING_LENGTH}. */
function isStringBound(max) {
    return Number.isInteger(max) && max >= 0 && max <= VALIDATOR_MAX_STRING_LENGTH;
}
/**
 * True when `value` holds more than `max` Unicode code points. A string of at most `max` UTF-16
 * units is within the bound and one of more than `2 * max` units is past it, so only the band
 * between is counted.
 */
function exceedsCodePoints(value, max) {
    if (value.length <= max)
        return false;
    return value.length > max * 2 || Array.from(value).length > max;
}
function checkString(schema, value, path) {
    const members = schema['enum'];
    const constant = schema['const'];
    const bounded = Object.hasOwn(schema, 'maxLength');
    const max = schema['maxLength'];
    if (Object.hasOwn(schema, 'enum') && !(Array.isArray(members) && members.every((m) => typeof m === 'string'))) {
        return reject(path, 'schema-unreadable');
    }
    if (Object.hasOwn(schema, 'const') && typeof constant !== 'string')
        return reject(path, 'schema-unreadable');
    const free = !Object.hasOwn(schema, 'enum') && !Object.hasOwn(schema, 'const');
    if ((free && !bounded) || (bounded && !isStringBound(max)))
        return reject(path, 'schema-unreadable');
    let pattern;
    if (Object.hasOwn(schema, 'pattern')) {
        const name = schema['pattern'];
        pattern = typeof name === 'string' ? COMPILED_PATTERNS.get(name) : undefined;
        if (pattern === undefined)
            return reject(path, 'schema-unreadable');
    }
    if (typeof value !== 'string')
        return reject(path, 'type');
    if (isStringBound(max) && exceedsCodePoints(value, max))
        return reject(path, 'length');
    if (Array.isArray(members) && !members.includes(value))
        return reject(path, 'enum');
    if (typeof constant === 'string' && value !== constant)
        return reject(path, 'const');
    if (pattern !== undefined && !pattern.test(value))
        return reject(path, 'pattern');
    return { ok: true, value };
}
function checkObject(schema, value, path, depth) {
    const properties = schema['properties'];
    const required = Object.hasOwn(schema, 'required') ? schema['required'] : [];
    if (schema['additionalProperties'] !== false || !isPlainObject(properties))
        return reject(path, 'schema-unreadable');
    if (!Array.isArray(required) || !required.every((name) => typeof name === 'string' && Object.hasOwn(properties, name))) {
        return reject(path, 'schema-unreadable');
    }
    if (!isPlainObject(value))
        return reject(path, 'type');
    for (const name of required) {
        if (!Object.hasOwn(value, name))
            return reject(`${path}/${token(name)}`, 'required');
    }
    const out = {};
    for (const key of Object.keys(properties)) {
        if (!Object.hasOwn(value, key))
            continue;
        const verdict = check(properties[key], value[key], `${path}/${token(key)}`, depth + 1);
        if (!verdict.ok)
            return verdict;
        Object.defineProperty(out, key, { value: verdict.value, enumerable: true, writable: true, configurable: true });
    }
    return { ok: true, value: out };
}
function checkArray(schema, value, path, depth) {
    if (!Object.hasOwn(schema, 'items'))
        return reject(path, 'schema-unreadable');
    if (!Array.isArray(value))
        return reject(path, 'type');
    const out = [];
    for (let i = 0; i < value.length; i += 1) {
        const verdict = check(schema['items'], value[i], `${path}/${i}`, depth + 1);
        if (!verdict.ok)
            return verdict;
        out.push(verdict.value);
    }
    return { ok: true, value: out };
}
/**
 * A `oneOf`: the first variant that accepts the value wins. Because the language's variants carry
 * distinct discriminator consts, at most one accepts. An unreadable variant makes the whole
 * schema unreadable; otherwise a value no variant accepts is `no-variant` at the union's path.
 */
function checkUnion(schema, value, path, depth) {
    const variants = schema['oneOf'];
    if (!keywordsAllowed(schema, new Set(['oneOf'])) || !Array.isArray(variants) || variants.length === 0) {
        return reject(path, 'schema-unreadable');
    }
    let accepted = null;
    for (const variant of variants) {
        const verdict = check(variant, value, path, depth + 1);
        if (!verdict.ok && verdict.reason === 'schema-unreadable')
            return verdict;
        if (verdict.ok && accepted === null)
            accepted = verdict;
    }
    return accepted ?? reject(path, 'no-variant');
}
//# sourceMappingURL=validate.js.map